import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { LocationObject } from 'expo-location';

import { type Store } from '@lib/entities/Store';
import { type DeferredAction, withDeferred } from '@lib/redux/deferred';
import { type Point } from '@lib/types/location';

/**
 * Redux twin of lib/state/StoreDirectory.ts + `useVisitStore` (JSAD-7433). The async actions
 * (`fetchOrigin` with its permission guard / 12 s race / `isValidPoint`, `fetchStores` with its
 * loading guard / MID_USA fallback / memo key, `init`, `visitStore`) are sagas; the reducers
 * below are the writes those sagas make. Not reset on `auth/loggedOut`.
 */
export interface StoreDirectoryState {
    isLoading: boolean;
    initialized: boolean;
    stores: Store[];
    currentStore?: Store;
    origin?: Point;
    showLocationPrompt: boolean;
    isLocationLoading: boolean;
    /** `undefined` means we don't have a response yet. */
    hasForegroundLocationPermissions: boolean | undefined;
    closedIndefinitely: number[];
    /**
     * serialized lat/lng memoization key for `fetchStores`
     */
    fetchStoresMemoKey?: string;
}

export type PersistedStoreDirectoryState = Pick<StoreDirectoryState, 'origin'>;

const initialState: StoreDirectoryState = {
    isLoading: false,
    initialized: false,
    stores: [],
    origin: undefined,
    currentStore: undefined,
    showLocationPrompt: false,
    isLocationLoading: false,
    hasForegroundLocationPermissions: undefined, // default to undefined so we can only fetch stores once we have an affirmative response
    closedIndefinitely: []
};

/** Saga-only request: the reducer is a no-op; the saga settles `meta.deferred`. */
const request = <P, T = void>() => ({
    reducer: (_state: unknown, _action: DeferredAction<P, T>) => {},
    prepare: withDeferred<P, T>()
});

/** `StoreDirectory.ts:133` fetchStores(lat?, lng?, options?) */
export interface FetchStoresParams {
    lat?: number;
    lng?: number;
    options?: { forceRefresh?: boolean; dontSelectStore?: boolean };
}

/**
 * `useVisitStore.ts:24` visitStore(storeOrId). `bookmarksEnabled` is the `menu-favorites-*`
 * flag read by the hook (`useMenu.ts:33-34`) and forwarded to the menu fetch the saga performs.
 */
export interface VisitStoreParams {
    storeOrId: Store | number;
    bookmarksEnabled: boolean;
}

/** `StoreDirectory.ts:164` `fetchStores.setStoresAndCurrent` */
export interface StoresFetchedPayload {
    stores: Store[];
    currentStore?: Store;
    memoKey: string;
}

const storeDirectorySlice = createSlice({
    name: 'storeDirectory',
    initialState,
    reducers: {
        changeStore: (state, action: PayloadAction<Store>) => {
            state.currentStore = action.payload;
        },
        setStores: (state, action: PayloadAction<Store[]>) => {
            state.stores = action.payload;
        },
        setOrigin: (state, action: PayloadAction<Point>) => {
            state.origin = action.payload;
        },
        resetOrigin: (state) => {
            state.origin = undefined;
        },
        /** `StoreDirectory.ts:78` `init.initialized` — the only write `init()` makes. */
        markInitialized: (state) => {
            state.initialized = true;
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        storesFetched: (state, action: PayloadAction<StoresFetchedPayload>) => {
            state.stores = action.payload.stores;
            state.currentStore = action.payload.currentStore;
            state.fetchStoresMemoKey = action.payload.memoKey;
        },
        /** `StoreDirectory.ts:173` — a failed fetch forgets its memo key so the next call retries. */
        storesFetchFailed: (state) => {
            state.fetchStoresMemoKey = undefined;
        },
        notAvailableForPickup: (state, action: PayloadAction<Store>) => {
            state.closedIndefinitely = state.closedIndefinitely.concat([action.payload.id]);
        },
        setIsLocationLoading: (state, action: PayloadAction<boolean>) => {
            state.isLocationLoading = action.payload;
        },
        setHasForegroundLocationPermissions: (
            state,
            action: PayloadAction<boolean | undefined>
        ) => {
            state.hasForegroundLocationPermissions = action.payload;
        },

        // ── Saga-only requests ─────────────────────────────────────────────────────────────
        fetchOriginRequest: request<void, LocationObject | undefined>(),
        fetchStoresRequest: request<FetchStoresParams>(),
        initRequest: request<void>(),
        visitStoreRequest: request<VisitStoreParams>()
    }
});

export const {
    changeStore,
    setStores,
    setOrigin,
    resetOrigin,
    markInitialized,
    setIsLoading,
    storesFetched,
    storesFetchFailed,
    notAvailableForPickup,
    setIsLocationLoading,
    setHasForegroundLocationPermissions,
    fetchOriginRequest,
    fetchStoresRequest,
    initRequest,
    visitStoreRequest
} = storeDirectorySlice.actions;

export const selectStoreDirectory = (state: { storeDirectory: StoreDirectoryState }) =>
    state.storeDirectory;
export const selectStores = (state: { storeDirectory: StoreDirectoryState }) =>
    state.storeDirectory.stores;
export const selectCurrentStore = (state: { storeDirectory: StoreDirectoryState }) =>
    state.storeDirectory.currentStore;
export const selectOrigin = (state: { storeDirectory: StoreDirectoryState }) =>
    state.storeDirectory.origin;
/** `StoreDirectory.ts:179` canOrderAhead(store) */
export const selectCanOrderAhead =
    (state: { storeDirectory: StoreDirectoryState }) =>
    (store: Store): boolean =>
        !state.storeDirectory.closedIndefinitely.includes(store.id);

export default storeDirectorySlice.reducer;
